import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, ExternalLink, Trash2 } from "lucide-react";
import api from "@/lib/api";
import RichEditor from "@/components/admin/RichEditor";
import ImageUpload from "@/components/tournament/ImageUpload";
import { inputCls as inp } from "@/components/tournament/constants";

const PAGES = [["about", "About"], ["contact", "Contact"], ["privacy", "Privacy Policy"], ["terms", "Terms"], ["help", "Help Center"]];

function PageEditor({ slug }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setP(null); api.get(`/pages/${slug}`).then((r) => setP(r.data)); }, [slug]);
  if (!p) return <div className="text-white/40 py-10">Loading…</div>;
  const save = async () => {
    setBusy(true);
    try { await api.put(`/admin/pages/${slug}`, { title: p.title, subtitle: p.subtitle || "", image: p.image || "", content: p.content || "" }); toast.success("Page saved"); }
    catch { toast.error("Save failed"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-4" data-testid={`page-editor-${slug}`}>
      <div className="grid md:grid-cols-[1fr_320px] gap-4">
        <div className="space-y-3">
          <div><label className="block text-xs font-semibold text-white/50 mb-1">Title</label><input value={p.title} onChange={(e) => setP({ ...p, title: e.target.value })} className={inp} data-testid="page-title-input" /></div>
          <div><label className="block text-xs font-semibold text-white/50 mb-1">Subtitle</label><input value={p.subtitle || ""} onChange={(e) => setP({ ...p, subtitle: e.target.value })} className={inp} data-testid="page-subtitle-input" /></div>
        </div>
        <div><label className="block text-xs font-semibold text-white/50 mb-1">Header image</label><ImageUpload value={p.image} onChange={(image) => setP({ ...p, image })} testid="page-image-upload" /></div>
      </div>
      <div><label className="block text-xs font-semibold text-white/50 mb-1">Content</label><RichEditor key={slug} value={p.content} onChange={(content) => setP((c) => ({ ...c, content }))} /></div>
      <div className="flex gap-3">
        <button onClick={save} disabled={busy} data-testid="page-save" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[hsl(var(--primary))] text-white font-bold disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save Page</button>
        <a href={`/${slug}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-white/15 text-white font-semibold"><ExternalLink size={15} /> View</a>
      </div>
    </div>
  );
}

function Messages() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/contact-messages").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  const del = async (id) => { await api.delete(`/admin/contact-messages/${id}`); setItems(items.filter((m) => m.id !== id)); };
  return (
    <div className="mt-10" data-testid="contact-messages">
      <h2 className="font-bold text-white mb-3">Contact messages ({items.length})</h2>
      <div className="space-y-2">
        {items.map((m) => (
          <div key={m.id} className="card-surface rounded-xl p-4 flex gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-xs text-white/40"><a href={`mailto:${m.email}`} className="font-semibold text-white">{m.name}</a> · {m.email} · {new Date(m.created_at).toLocaleString()}</div>
              {m.subject && <div className="font-semibold text-white mt-1">{m.subject}</div>}
              <p className="text-sm text-white/70 mt-1 whitespace-pre-line break-words">{m.message}</p>
            </div>
            <button onClick={() => del(m.id)} className="self-start p-2 text-white/40 hover:text-red-400"><Trash2 size={15} /></button>
          </div>
        ))}
        {!items.length && <div className="card-surface rounded-xl p-6 text-center text-white/40">No messages yet.</div>}
      </div>
    </div>
  );
}

export default function AdminPages() {
  const [tab, setTab] = useState("about");
  return (
    <div data-testid="admin-pages">
      <h1 className="text-2xl font-bold text-white">Pages</h1>
      <p className="text-sm text-white/50 mb-5">Edit About, Contact, Privacy Policy and Terms with text and pictures.</p>
      <div className="flex gap-2 mb-6">
        {PAGES.map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`pages-tab-${k}`} className={`px-4 py-1.5 rounded-full text-sm font-semibold ${tab === k ? "bg-white text-black" : "card-surface text-white/60 hover:text-white"}`}>{l}</button>)}
      </div>
      <PageEditor slug={tab} />
      {tab === "contact" && <Messages />}
    </div>
  );
}
